import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { PrincipalDTO } from '@propmgt/shared';
import { api } from '../api/client';

/**
 * Auth context — loads GET /api/auth/session once on mount, exposes the resolved `principal` +
 * `csrfToken`, and wires the CSRF token onto the shared client so every mutation carries it.
 * `setCsrfToken` re-exposes the client's setter (login/MFA rotate the session id → a new token).
 * `refresh` re-reads the session after a login/logout so the router re-evaluates the shell.
 */
interface AuthState {
  status: 'loading' | 'authenticated' | 'anonymous';
  principal: PrincipalDTO | null;
  csrfToken: string | null;
  setCsrfToken: (token: string | null) => void;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthState['status']>('loading');
  const [principal, setPrincipal] = useState<PrincipalDTO | null>(null);
  const [csrfToken, setCsrfTokenState] = useState<string | null>(null);

  const setCsrfToken = useCallback((token: string | null) => {
    api.setCsrfToken(token);
    setCsrfTokenState(token);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const res = await api.auth.session();
      // The shared client already adopts the token in `auth.session()`; mirror it into state.
      setCsrfTokenState(res.csrfToken);
      setPrincipal(res.principal);
      setStatus('authenticated');
    } catch {
      setPrincipal(null);
      setCsrfTokenState(null);
      setStatus('anonymous');
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.auth.logout();
    } catch {
      /* best-effort — clear locally regardless */
    }
    api.setCsrfToken(null);
    setPrincipal(null);
    setCsrfTokenState(null);
    setStatus('anonymous');
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<AuthState>(
    () => ({ status, principal, csrfToken, setCsrfToken, refresh, logout }),
    [status, principal, csrfToken, setCsrfToken, refresh, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
